import { useState } from 'react';

import { BarChart } from '@/components/ui/BarChart';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { platformSupportApi, type TicketScopeParams } from '../api/support.api';

const MONTH_OPTIONS = [
  { value: '6', label: 'Last 6 months' },
  { value: '12', label: 'Last 12 months' },
  { value: '24', label: 'Last 24 months' },
];

const SERIES = [
  { key: 'open' as const, label: 'Still open', tone: 1 as const },
  { key: 'closed' as const, label: 'Resolved or closed', tone: 2 as const },
];

/** `2026-10` → "October 2026" and "Oct". */
function monthLabels(month: string) {
  const date = new Date(`${month}-01T00:00:00`);
  return {
    label: date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
    axisLabel: date.toLocaleDateString('en-IN', { month: 'short' }),
  };
}

/**
 * Tickets raised each month, stacked by where they stand now — so a tall bar
 * that is mostly "still open" is the month that has not been dealt with.
 *
 * Follows the queue's school, state, person and date filters, so choosing a
 * school turns this into that school's history.
 */
export function TicketTrendChart({ scope }: { scope: TicketScopeParams }) {
  const [months, setMonths] = useState(12);
  const { data, isLoading, error, refetch } = useApiQuery(
    () => platformSupportApi.trend({ ...scope, months }),
    [months, scope.institute, scope.state, scope.raisedBy, scope.from, scope.to, scope.search],
  );

  const total = data?.reduce((sum, month) => sum + month.open + month.closed, 0) ?? 0;

  return (
    <section className="card flex flex-col gap-4 p-4 sm:p-5 shadow-xs border border-line bg-surface" aria-labelledby="trend-heading">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-line pb-3">
        <div>
          <h2 id="trend-heading" className="text-base font-[650] text-ink">
            Tickets raised per month
          </h2>
          <p className="mt-0.5 text-xs text-ink-3">
            {data
              ? `${total} ticket${total === 1 ? '' : 's'} in this period, for the filters below.`
              : 'For the filters below.'}
          </p>
        </div>
        <div className="w-full sm:w-48">
          <Select
            label="Period"
            hideLabel
            options={MONTH_OPTIONS}
            value={String(months)}
            onChange={(event) => setMonths(Number(event.target.value))}
          />
        </div>
      </header>

      {error ? (
        <QueryError title="Could not load the chart" error={error} onRetry={refetch} />
      ) : isLoading || !data ? (
        <SkeletonLines count={5} />
      ) : total === 0 ? (
        <p className="py-8 text-center text-sm text-ink-3">No tickets were raised in this period.</p>
      ) : (
        <BarChart
          title="Tickets raised per month"
          series={SERIES}
          points={data.map((month) => ({
            ...monthLabels(month.month),
            values: { open: month.open, closed: month.closed },
          }))}
          height={220}
        />
      )}
    </section>
  );
}
